// Chunk Loader with Retry Logic
// Handles failed dynamic imports with automatic retry and fallback
import React from 'react';

interface ChunkLoadError extends Error {
  type: 'chunk-load-error';
  chunkName?: string;
  url?: string;
}

/**
 * Load a chunk with retry logic
 */
export async function loadChunkWithRetry<T>(
  importFn: () => Promise<T>,
  maxRetries: number = 3,
  retryDelay: number = 1000
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await importFn();
    } catch (error) {
      lastError = error as Error;
      
      // Check if it's a chunk load error
      const isChunkError = 
        error instanceof TypeError && 
        (error.message.includes('Failed to fetch') || 
         error.message.includes('dynamically imported module') ||
         error.message.includes('Loading chunk') ||
         error.message.includes('Loading CSS chunk'));
      
      if (!isChunkError) {
        // Not a chunk error, don't retry
        throw error;
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        console.error(`❌ Failed to load chunk after ${maxRetries + 1} attempts:`, error);
        
        // Create a chunk load error
        const chunkError: ChunkLoadError = new Error(
          `Failed to load chunk after ${maxRetries + 1} attempts. Please refresh the page.`
        ) as ChunkLoadError;
        chunkError.type = 'chunk-load-error';
        chunkError.name = 'ChunkLoadError';
        
        // Try to extract chunk name from error message
        const urlMatch = error.message?.match(/https?:\/\/[^\s]+/);
        if (urlMatch) {
          chunkError.url = urlMatch[0];
          const chunkNameMatch = urlMatch[0].match(/\/([^\/]+)\.js/);
          if (chunkNameMatch) {
            chunkError.chunkName = chunkNameMatch[1];
          }
        }
        
        throw chunkError;
      }
      
      // Wait before retrying with exponential backoff
      const delay = retryDelay * Math.pow(2, attempt);
      console.warn(`⚠️ Chunk load failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Failed to load chunk');
}

/**
 * Create a lazy component with retry logic
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  maxRetries: number = 3
): React.LazyExoticComponent<T> {
  return React.lazy(() => loadChunkWithRetry(importFn, maxRetries));
}

